/**
 * Vulnerability Managed Dropdown Component
 *
 * Consolidated dropdown that combines three vulnerability edit actions:
 * - Change Status (with nested submenu)
 * - Change Severity (with nested submenu)
 * - Add Aux Info (direct action)
 *
 * Uses the Menu submenu pattern for nested navigation.
 *
 * @example
 * ```tsx
 * <VulnerabilityManagedDropdown
 *   risks={selectedVulnerabilities}
 *   onUpdateRisk={handleUpdateRisk}
 *   getRiskStatusLabel={getRiskStatusLabel}
 *   severityOptions={severityFilterOptions}
 *   onSeverityChange={handleSeverityChange}
 *   onAuxInfoClick={onAuxInfoClick}
 *   disabled={false}
 * />
 * ```
 *
 * @module components/actions
 */

import {
  ArrowPathIcon,
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import { Dropdown } from '@/components/Dropdown';
import { getBaseRiskStatusOptions } from '@/components/legacy-ui/RiskDropdown';
import { MenuItemProps } from '@/components/Menu';
import { Modal } from '@/components/Modal';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { useAuth } from '@/state/auth';
import { Risk, RiskDeletedSubStatus, RiskStatus } from '@/types';
import type { Severity } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Props for VulnerabilityManagedDropdown component
 *
 * @template T - Type of risk items (must have key and status, typically Risk)
 */
export interface VulnerabilityManagedDropdownProps<
  T extends { key: string; status: string } = Risk,
> {
  /** Risks to edit */
  risks: T[];
  /** Handler for updating risk status */
  onUpdateRisk?: (risk: T, comment?: string) => Promise<void>;
  /** Function to parse risk status */
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  /** Severity options for dropdown */
  severityOptions?: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  /** Handler for severity change */
  onSeverityChange?: (option: {
    value?: string | number;
    label: string;
    severity?: string;
  }) => void;
  /** Handler for opening auxiliary info modal */
  onAuxInfoClick?: () => void;
  /** Handler for refreshing visited timestamp (Praetorian users only) */
  onRefreshVisited?: (comment: string) => void;
  /** Whether the refresh visited mutation is in progress */
  isRefreshingVisited?: boolean;
  /** Disable the dropdown */
  disabled?: boolean;
  /** Test ID for dropdown */
  'data-testid'?: string;
  /** Custom button label (default: "Manage") */
  label?: string;
  /** Custom button icon (default: PencilIcon) */
  icon?: ReactNode;
}

/**
 * Close reason options for reject modal
 */
const closeReasons = [
  {
    label: 'False Positive',
    value: RiskDeletedSubStatus.FalsePositive,
    description:
      'This risk is deemed to be a false positive or not valid, and no further action will be taken.',
  },
  {
    label: 'Out of Scope',
    value: RiskDeletedSubStatus.OutOfScope,
    description:
      'This risk is out of scope and will not be addressed or tracked.',
  },
  {
    label: 'Duplicate',
    value: RiskDeletedSubStatus.Duplicate,
    description:
      'This risk is a duplicate of another risk and will not be addressed or tracked.',
  },
  {
    label: 'Other',
    value: RiskDeletedSubStatus.Other,
    description: 'There is something else that is not listed above.',
  },
];

/**
 * Get status options for dropdown based on user type and mode
 * @param isPraetorianUser - Whether user is a Praetorian user
 * @param mode - The current vulnerability status mode
 * @returns Status options including Pending for Praetorian users
 */
const getStatusOptions = (
  isPraetorianUser: boolean,
  mode: 'legacy' | 'new'
) => [
  ...getBaseRiskStatusOptions(isPraetorianUser, mode),
  {
    label: getStatusLabel(RiskStatus.Deleted, mode),
    value: 'reject',
    icon: <XCircleIcon className="size-4" />,
    count: 'R',
  },
];

/**
 * Vulnerability Managed Dropdown Component
 *
 * Renders a dropdown with Change Status, Change Severity submenus
 * and Add Aux Info direct action.
 *
 * @template T - Type of risk items (must have key and status)
 * @param props - Component props
 * @returns Dropdown component with modal
 */
export function VulnerabilityManagedDropdown<
  T extends { key: string; status: string } = Risk,
>({
  risks,
  onUpdateRisk,
  getRiskStatusLabel: _getRiskStatusLabel,
  severityOptions = [],
  onSeverityChange,
  onAuxInfoClick,
  onRefreshVisited,
  isRefreshingVisited = false,
  disabled = false,
  'data-testid': testId = 'vulnerability-managed-dropdown',
  label = 'Manage',
  icon,
}: VulnerabilityManagedDropdownProps<T>) {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isRefreshVisitedModalOpen, setIsRefreshVisitedModalOpen] =
    useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isPraetorianUser } = useAuth();
  const { mode } = useVulnerabilityStatusMode();

  // Get status options filtered by user type with mode-aware labels
  const statusOptions = getStatusOptions(isPraetorianUser, mode);

  const handleStatusChange = async (selectedValue: string) => {
    if (selectedValue === 'reject') {
      setIsDropdownOpen(false);
      setIsCloseModalOpen(true);
      return;
    }

    if (!onUpdateRisk) return;

    try {
      const count = risks.length;
      const statusLabel = getStatusLabel(selectedValue as RiskStatus, mode);

      await Promise.all(
        risks.map(async risk => {
          const currentSeverity = risk.status.length > 1 ? risk.status[1] : '';
          const combinedStatus = `${selectedValue}${currentSeverity}`;
          const updatedRisk = {
            ...risk,
            status: combinedStatus,
            showSnackbar: false,
          } as T;
          await onUpdateRisk(updatedRisk);
        })
      );
      toast.success(
        count === 1
          ? `Risk updated to ${statusLabel}`
          : `${count} risks updated to ${statusLabel}`
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Build Change Status submenu items
  const statusSubmenuItems: MenuItemProps[] = statusOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
    icon: opt.icon,
    count: opt.count,
    onClick: () => handleStatusChange(opt.value),
  }));

  // Build Change Severity submenu items
  const severitySubmenuItems: MenuItemProps[] = severityOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
    severity: opt.label.toLowerCase() as Severity,
    onClick: () => onSeverityChange?.(opt),
  }));

  // Build menu items
  const menuItems: MenuItemProps[] = [
    // Change Status with nested submenu
    ...(onUpdateRisk
      ? [
          {
            label: 'Change Status',
            icon: <ACTION_ICONS.STATUS_CHANGE className="size-4" />,
            submenu: statusSubmenuItems,
          },
        ]
      : []),
    // Change Severity with nested submenu
    ...(onSeverityChange && severityOptions.length > 0
      ? [
          {
            label: 'Change Severity',
            icon: <ACTION_ICONS.SEVERITY_CHANGE className="size-4" />,
            submenu: severitySubmenuItems,
          },
        ]
      : []),
    // Add Aux Info direct action
    ...(onAuxInfoClick
      ? [
          {
            label: 'Add Aux Info',
            icon: <PencilIcon className="size-4" />,
            onClick: onAuxInfoClick,
          },
        ]
      : []),
    // Refresh Last Seen (Praetorian users only)
    ...(onRefreshVisited && isPraetorianUser
      ? [
          {
            label: 'Refresh Last Seen',
            icon: <ArrowPathIcon className="size-4" />,
            onClick: () => {
              setIsDropdownOpen(false);
              setIsRefreshVisitedModalOpen(true);
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <div data-testid={testId} className="w-fit">
        <Dropdown
          label={label}
          startIcon={icon ?? <PencilIcon className="size-4" />}
          styleType="rounded"
          size="sm"
          disabled={disabled}
          menu={{
            items: menuItems,
            placement: 'bottom-start',
            open: isDropdownOpen,
            onOpenChange: setIsDropdownOpen,
          }}
        />
      </div>
      <CloseModal
        open={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        risks={risks}
        onUpdateRisk={onUpdateRisk}
      />
      <RefreshVisitedModal
        key={String(isRefreshVisitedModalOpen)}
        open={isRefreshVisitedModalOpen}
        onClose={() => setIsRefreshVisitedModalOpen(false)}
        onConfirm={(comment: string) => {
          onRefreshVisited?.(comment);
          setIsRefreshVisitedModalOpen(false);
        }}
        isRefreshing={isRefreshingVisited}
        count={risks.length}
      />
    </>
  );
}

/**
 * Props for CloseModal component
 *
 * @template T - Type of risk items (must have key and status)
 */
interface CloseModalProps<T extends { key: string; status: string }> {
  open: boolean;
  onClose: () => void;
  risks: T[];
  onUpdateRisk?: (risk: T, comment?: string) => Promise<void>;
}

/**
 * Modal for selecting close reason when rejecting risks
 *
 * @template T - Type of risk items (must have key and status)
 */
const CloseModal = <T extends { key: string; status: string }>({
  open,
  onClose,
  risks,
  onUpdateRisk,
}: CloseModalProps<T>) => {
  const [selectedCloseReason, setSelectedCloseReason] = useState<string>('');

  const handleCloseConfirm = async () => {
    if (!selectedCloseReason || !onUpdateRisk) return;

    try {
      const closeReason = closeReasons.find(
        r => r.value === selectedCloseReason
      );

      const count = risks.length;
      await Promise.all(
        risks.map(async risk => {
          const currentSeverity = risk.status.length > 1 ? risk.status[1] : '';
          const updatedRisk = {
            ...risk,
            status: `${RiskStatus.Deleted}${currentSeverity}${closeReason?.value}`,
            showSnackbar: false,
          } as T;
          await onUpdateRisk(updatedRisk);
        })
      );
      toast.success(count === 1 ? 'Risk rejected' : `${count} risks rejected`);

      onClose();
      setSelectedCloseReason('');
    } catch {
      console.error('Failed to update risk status');
      toast.error('Failed to reject risk. Please try again.');
    }
  };

  return (
    <Modal
      title="Reject Risk"
      open={open}
      onClose={() => {
        onClose();
        setSelectedCloseReason('');
      }}
      footer={{
        text: 'Confirm',
        onClick: handleCloseConfirm,
        disabled: !selectedCloseReason,
      }}
      className="py-0"
    >
      <div className="space-y-4 py-2 pt-0">
        <hr className="border-chariot-stroke w-full" />
        <p className="text-chariot-text-secondary text-sm">
          {risks.length === 1
            ? 'Select a reason for closing this risk:'
            : `Select a reason for closing these ${risks.length} risks:`}
        </p>
        <div className="space-y-2">
          {closeReasons.map((reason, key) => (
            <Radio
              key={key}
              selected={selectedCloseReason}
              label={reason.label}
              description={reason.description}
              value={reason.value}
              onChange={setSelectedCloseReason}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

/**
 * Radio button component for close reason selection
 */
const Radio = ({
  selected,
  label,
  description,
  value,
  onChange,
}: {
  selected: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const checked = selected === value;
  return (
    <label
      className={cn(
        'border-chariot-stroke hover:bg-chariot-background-secondary flex items-start gap-3 rounded-md border p-4',
        checked && 'bg-chariot-background-secondary'
      )}
    >
      <input
        type="radio"
        name="deletedStatus"
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-chariot-text-secondary text-sm">{description}</div>
      </div>
    </label>
  );
};

/**
 * Props for RefreshVisitedModal component
 */
interface RefreshVisitedModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  isRefreshing: boolean;
  count: number;
}

const DEFAULT_VISITED_COMMENT =
  "At the time of Praetorian's review, Praetorian observed that the risk was still present.";

/**
 * Modal for confirming refresh of visited timestamp
 */
const RefreshVisitedModal = ({
  open,
  onClose,
  onConfirm,
  isRefreshing,
  count,
}: RefreshVisitedModalProps) => {
  const [comment, setComment] = useState(DEFAULT_VISITED_COMMENT);

  // No useEffect needed - key prop on parent forces remount which resets state

  return (
    <Modal
      title="Refresh Last Seen"
      open={open}
      onClose={onClose}
      footer={{
        text: 'Confirm',
        onClick: () => onConfirm(comment),
        disabled: isRefreshing,
      }}
      className="py-0"
    >
      <div className="space-y-4 py-2 pt-0">
        <hr className="border-chariot-stroke w-full" />
        <p className="text-chariot-text-secondary text-sm">
          {count === 1
            ? 'Are you sure you want to refresh the last seen timestamp for this risk?'
            : `Are you sure you want to refresh the last seen timestamp for these ${count} risks?`}
        </p>
        <p className="text-chariot-text-secondary text-sm">
          This will update the &quot;visited&quot; field. If a comment is
          provided, a history item will also be added.
        </p>
        <div>
          <label
            htmlFor="visited-comment"
            className="text-chariot-text-secondary mb-1 block text-sm font-medium"
          >
            Comment
          </label>
          <textarea
            id="visited-comment"
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={3}
            className="border-chariot-stroke bg-chariot-layer0 text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          />
        </div>
      </div>
    </Modal>
  );
};
